import { ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import LoadingOverlay from './ui/LoadingOverlay';

export type PageWithLoadingOverlayProps = {
  children: ReactNode;
};

const PageWithLoadingOverlayContext = createContext<{
  showLoadingOverlay: () => void;
  hideLoadingOverlay: () => void;
}>({
  showLoadingOverlay: () => {},
  hideLoadingOverlay: () => {},
});

export const usePageWithLoadingOverlay = () => {
  const context = useContext(PageWithLoadingOverlayContext);
  if (context === undefined) {
    throw new Error('usePageWithLoadingOverlay must be used within a PageWithLoadingOverlayProvider');
  }
  return context;
};

const LOADING_OVERLAY_DELAY_MS = 800;

const PageWithLoadingOverlay = ({ children }: PageWithLoadingOverlayProps) => {
  const [overlayOpen, setOverlayOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showLoadingOverlay = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      setOverlayOpen(true);
    }, LOADING_OVERLAY_DELAY_MS);
  }, []);

  const hideLoadingOverlay = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setOverlayOpen(false);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <PageWithLoadingOverlayContext.Provider value={{ showLoadingOverlay, hideLoadingOverlay }}>
      {overlayOpen && <LoadingOverlay />}
      {children}
    </PageWithLoadingOverlayContext.Provider>
  );
};

export default PageWithLoadingOverlay;
